import { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Typography, Avatar, makeStyles } from '@material-ui/core';
import CustomLink from '../../shared/customLink';
import PageContainer from '../../shared/pageContainer';
import { ReactComponent as NoAccessIcon } from '../../../assets/icons/domain_disabled.svg';
import { APP_PAGES } from '../../../constants/pages';
import { useTranslations } from '../../../utility/useTranslations';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainAuthorized: {
    boxSizing: 'border-box',
    minHeight: 'calc(100vh - 153px)',
    paddingTop: spacing(12),
  },
  title: {
    marginBottom: spacing(3),
  },
  iconSize: {
    width: 120,
    height: 120,
  },
  avatarSection: {
    marginBottom: '1em',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  avatar: {
    width: 160,
    height: 160,
    backgroundColor: primary.pink1,
  },
}));

const UnauthorizedAccessContent = ({
  translations,
  isAuthorized,
  navigate,
}) => {
  const classes = useStyles();

  return (
    <div
      className={classNames(classes.main, {
        [classes.mainAuthorized]: isAuthorized,
      })}
    >
      <div className={classes.avatarSection}>
        <Avatar className={classes.avatar}>
          <NoAccessIcon className={classes.iconSize} />
        </Avatar>
      </div>
      <Typography variant="h1" className={classes.title}>
        {translations.pageTitle}
      </Typography>
      <CustomLink
        text={translations.goHome}
        handleClick={() => navigate('/')}
      />
    </div>
  );
};

UnauthorizedAccessContent.propTypes = {
  translations: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
};

const UnauthorizedAccessPage = ({ auth, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const translations = useTranslations(APP_PAGES.UNAUTHORIZED_ACCESS);
  const isAuthorized = !!(auth && auth?.access);

  const content = (
    <UnauthorizedAccessContent
      translations={translations}
      navigate={navigate}
      isAuthorized={isAuthorized}
    />
  );

  if (isAuthorized) {
    return (
      <PageContainer
        {...rest}
        auth={auth}
        translations={translations}
        location={location}
        navigate={navigate}
        shouldPassProps={false}
        isFullWidthContent
        shouldRemoveSpacing
      >
        <UnauthorizedAccessContent
          translations={translations}
          navigate={navigate}
          isAuthorized={isAuthorized}
        />
      </PageContainer>
    );
  }

  return content;
};

export default memo(UnauthorizedAccessPage);
